// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § Reminders and emails (access-ending notice + extend), § Portal routes
// ==============================================
// /dashboard/papers/requests/:rid/extend?vessel=<vid>&recipient=<recId> — where
// "Ask <name> to extend" in the access-ending email lands. The skipper sees
// that crew member's shared papers and when access ends, picks the date to ask
// for (a month past the current end, by default), and asks: the crew member is
// emailed a one-tap approve link, and the skipper is emailed their answer.
// Nothing under 13 px (readability.test.ts).
// ==============================================

import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../services/api';
import { apiError, waitLabel } from '../apiError';
import { accessEndDate, addDays, firstName, fmtDay, fmtDayYear, isDate, passageLabel, today } from '../dates';
import { FormError, S } from './skipperUi';

/** Access may run at most this far ahead (the API's grant limit is 400 days). */
const MAX_AHEAD_DAYS = 399;

/** The vessel a request of mine is on: the ?vessel hint, else a look through my skipper vessels. */
async function vesselFor(rid: string, hint: string | null): Promise<string> {
  if (hint) return hint;
  const access = await api.papersAccess();
  for (const v of access.vessels.filter((x) => x.role === 'owner' || x.role === 'captain')) {
    const list = await api.skipperRequests(v.id).catch(() => []);
    if (list.some((r) => r.id === rid)) return v.id;
  }
  throw new Error('This request isn’t one of yours, or it no longer exists.');
}

export function ExtendAskPage() {
  const { rid = '' } = useParams();
  const [params] = useSearchParams();
  const recId = params.get('recipient') ?? '';
  const vesselQ = useQuery({ queryKey: ['skipper-request-vessel', rid, params.get('vessel')], queryFn: () => vesselFor(rid, params.get('vessel')), retry: false });
  const vid = vesselQ.data;
  const q = useQuery({ queryKey: ['skipper-request', vid, rid], queryFn: () => api.skipperRequest(vid as string, rid), enabled: !!vid, retry: false });
  const d = q.data;
  const rec = d?.recipients.find((r) => r.id === recId) ?? null;
  const shared = useMemo(() => (rec?.items ?? []).filter((i) => i.grant && !i.grant.revokedAt), [rec]);
  const tz = d?.timezone ?? 'UTC';
  const ends = shared.map((i) => accessEndDate((i.grant as { expiresAt: string }).expiresAt, tz)).sort();
  const earliest = ends[0] ?? null;
  const latest = ends[ends.length - 1] ?? null;
  const now = today(tz);
  const max = addDays(now, MAX_AHEAD_DAYS);
  const suggested = latest ? (addDays(latest, 30) > max ? max : addDays(latest, 30)) : '';

  const [until, setUntil] = useState<string | null>(null);
  const value = until ?? suggested;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  const crew = firstName(rec?.name ?? rec?.email.split('@')[0], 'them');
  const back = `/dashboard/papers/requests/${rid}${vid ? `?vessel=${vid}` : ''}`;

  const ask = async () => {
    if (!vid || !rec) return;
    if (!isDate(value) || (earliest && value <= earliest) || value > max) { setError(`Choose a date after ${fmtDay(earliest)} and before ${fmtDayYear(max)}.`); return; }
    setBusy(true); setError(null); setSent(null);
    try {
      const r = await api.skipperAskExtend(vid, rid, rec.id, value);
      if (r.emailed) setSent(`We emailed ${crew} a one-tap link to keep access until ${fmtDayYear(r.untilOn)}. We’ll email you their answer.`);
      else setError(`We couldn’t email ${crew} just now. Try again in a few minutes.`);
    } catch (err) {
      const e = apiError(err, 'That could not be sent.');
      setError(e.status === 429 && e.retryAfter ? `${e.message}. Try again in ${waitLabel(e.retryAfter)}.` : e.message);
    } finally { setBusy(false); }
  };

  if (vesselQ.isLoading || q.isLoading) return <div className="p-10 text-center text-base text-slate-400" role="status">Loading…</div>;
  if (vesselQ.error || q.error || !d) {
    return (
      <div className="mx-auto max-w-xl space-y-3 p-6">
        <FormError>{vesselQ.error ? (vesselQ.error as Error).message : apiError(q.error, 'This request could not be opened.').message}</FormError>
        <Link to="/dashboard/papers/requests" className={S.link}>All my requests</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5 p-3 sm:p-6">
      <nav aria-label="Breadcrumb" className={S.label}>
        <Link to={back} className={S.link}>{passageLabel(d.fromPlace, d.toPlace) ?? 'The request'}</Link>
      </nav>
      <h1 className="m-0 text-2xl font-bold text-white">Ask {rec ? (rec.name ?? rec.email) : 'crew'} to extend your access</h1>

      {!rec ? (
        <FormError>That person isn’t on this request any more.</FormError>
      ) : !shared.length ? (
        <div className={`${S.card} p-5`}><p className="m-0 text-base text-slate-300">{crew} hasn’t shared anything with you on this request, so there is nothing to extend.</p></div>
      ) : (
        <div className={`${S.card} flex flex-col gap-4 p-5`}>
          <div className="flex flex-col gap-1">
            <span className={S.label}>Shared with you</span>
            <ul className="m-0 flex list-none flex-col p-0">
              {shared.map((i) => (
                <li key={i.id} className="flex items-center justify-between gap-3 border-b border-slate-700 py-3 text-base text-white last:border-b-0">
                  <span>{i.label}</span>
                  <span className="text-sm text-slate-400">{i.grant?.active ? `until ${fmtDay(accessEndDate(i.grant.expiresAt, tz))}` : 'access ended'}</span>
                </li>
              ))}
            </ul>
          </div>
          <label className={S.field} htmlFor="extend-until">
            Keep access until the end of
            <input id="extend-until" type="date" className={S.input} value={value} min={earliest ? addDays(earliest, 1) : undefined} max={max}
              onChange={(e) => { setUntil(e.target.value); setError(null); setSent(null); }} aria-describedby="extend-help" />
          </label>
          <p id="extend-help" className={S.note}>{crew} gets an email with a one-tap link to approve or decline. Nothing changes unless they approve, and they can still change the date or withdraw.</p>
          <FormError>{error}</FormError>
          {sent && <p role="status" className="m-0 rounded-xl border border-emerald-400/50 bg-emerald-500/10 px-4 py-3 text-base text-emerald-200">{sent}</p>}
          <div className="flex flex-wrap gap-2">
            <button type="button" className={S.primary} disabled={busy || !value} onClick={() => void ask()}>{busy ? 'Sending…' : `Ask ${crew}`}</button>
            <Link to={back} className={S.ghost}>Back to the request</Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExtendAskPage;
